import React, { useEffect, useMemo, useState } from 'react';
import { Search, UserPlus } from 'lucide-react';
import { useAllTenantUsers, useSearchUsers } from '../hooks/useSAMUsers';
import type { SAMUser } from '../types/role.types';
import { useBusinesses, type Business } from '../hooks/useBusinessAccessType';
import { AddBusinessUserSheet } from './AddBusinessUserSheet';
import { Notice } from './ui/Notice';
import { TableSkeleton } from './TableSkeleton';
import { useT, type MessageKey, type TFunction } from '../i18n/admin';
import { useFdCan } from '../hooks/useFdCan';
import { useUserPermissions } from '../hooks/useUserPermissions';

const initials = (u: SAMUser): string =>
  ((u.name?.[0] || '') + (u.family_name?.[0] || '')).toUpperCase() || (u.email?.[0] || '?').toUpperCase();

// Cognito group → chip text. Unknown groups keep their own name.
// SAM-1323: the chip names the role of Team (Owner, Admin, Operator), not the group that carries it: today's Operator
// is the front desk group `business_invitation`, and Team v3's Operator brings `business_operator` with it.
const ROLE_CHIP: Record<string, MessageKey> = {
  business_owner: 'team.chip.owner',
  business_admin: 'team.chip.admin',
  business_manager: 'team.chip.admin',
  business_operator: 'team.chip.operator',
  business_invitation: 'team.chip.operator',
};

export const businessRoles = (u: SAMUser, t: TFunction): string[] => {
  const chips = (u.groups || [])
    .filter((g) => g.startsWith('business'))
    .map((g) => (ROLE_CHIP[g] ? t(ROLE_CHIP[g]) : g.replace('business_', '').replace(/_/g, ' ')));
  return [...new Set(chips)];
};

type TeamStatus = { label: MessageKey; cls: string };
const teamStatus = (u: SAMUser): TeamStatus => {
  if (!u.enabled) return { label: 'common.status.inactive', cls: 'cancelled' };
  if (u.status === 'FORCE_CHANGE_PASSWORD') return { label: 'team.status.invited', cls: 'pending' };
  return { label: 'common.status.active', cls: 'confirmed' };
};

/**
 * Team members of a single business. Runs the same user search as the
 * settings/admin-users page (useAllTenantUsers('admin') + useSearchUsers) but
 * filters down to users whose `custom:business_id` matches the open business. The list asks the server for that
 * business only (`business_id`, SAM-1153); the client filter stays as a net for a backend that ignores it.
 */
const AdminTeamList: React.FC<{ businessId: string; onOpen?: (member: SAMUser) => void }> = ({ businessId, onOpen }) => {
  const { t } = useT();
  const [searchTerm, setSearchTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [adding, setAdding] = useState(false);
  const canAddMember = useFdCan('team', 'create');
  const { data: businessesData } = useBusinesses();
  const businesses: Business[] = useMemo(() => {
    const raw = businessesData as Business[] | { data?: Business[] } | undefined;
    if (Array.isArray(raw)) return raw;
    return raw && 'data' in raw && Array.isArray(raw.data) ? raw.data : [];
  }, [businessesData]);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(searchTerm), 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const {
    users: allUsers,
    isLoading: loadingAll,
    isFetchingMore,
    isError: errorAll,
    refetch: refetchAll,
  } = useAllTenantUsers('admin', { businessId });
  const isSearchActive = debounced.length > 0;
  const {
    users: searchResults,
    isLoading: searching,
    isError: errorSearch,
    refetch: refetchSearch,
  } = useSearchUsers(debounced, isSearchActive);

  const source = isSearchActive ? searchResults : allUsers;
  const isLoading = isSearchActive ? searching : loadingAll;
  const isError = isSearchActive ? errorSearch : errorAll;
  const retry = isSearchActive ? refetchSearch : refetchAll;

  const team = useMemo(
    () =>
      (source || []).filter(
        (u) => String(u.custom_attributes?.business_id ?? '') === String(businessId),
      ),
    [source, businessId],
  );

  // The full list arrives page by page: an empty team is only believable once
  // the last page is in, and a typing pause before the search fires is not "no results".
  const settling = isSearchActive ? debounced !== searchTerm : isFetchingMore;
  const showLoading = isLoading || (team.length === 0 && !isError && settling);

  return (
    <div>
      <div className="bz-pay-filters">
        <div className="bz-search bz-pay-search">
          <span className="bz-search-icon"><Search size={14} /></span>
          <input
            className="bz-search-input"
            placeholder={t('team.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        {canAddMember && (
          <button type="button" className="btn primary" onClick={() => setAdding(true)}>
            <UserPlus size={14} /> {t('team.addMember')}
          </button>
        )}
      </div>
      <AddBusinessUserSheet
        isOpen={adding}
        onClose={() => setAdding(false)}
        businessId={businessId}
        businesses={businesses}
      />

      {showLoading ? (
        <TableSkeleton rows={5} bare label={t('team.loading')} />
      ) : team.length === 0 && isError ? (
        <Notice variant="error" style={{ margin: '20px 0' }}>
          {t('team.error')}{' '}
          <button type="button" className="btn" onClick={() => retry()}>
            {t('team.retry')}
          </button>
        </Notice>
      ) : team.length === 0 ? (
        <div className="bz-list-empty" role="status">
          {isSearchActive ? t('team.emptySearch') : t('team.empty')}
        </div>
      ) : (
        <>
        {/* On a phone the table does not fit a card (SAM-1075, QA T3): one row per member instead, the name on top and the role and status under it. */}
        <ul className="m-0 p-0 list-none flex flex-col md:hidden" aria-label={t('team.col.member')} data-testid="team-rows">
          {team.map((u) => {
            const st = teamStatus(u);
            const roles = businessRoles(u, t);
            return (
              <li key={u.username} style={{ borderTop: '1px solid var(--line-subtle)' }}>
                <button
                  type="button"
                  onClick={onOpen ? () => onOpen(u) : undefined}
                  className="w-full text-left bg-transparent border-0 py-3 px-1 flex flex-col gap-2"
                  style={{ cursor: onOpen ? 'pointer' : 'default' }}
                >
                  <span className="bz-team-person">
                    <span className="bz-team-avatar">{initials(u)}</span>
                    <span className="bz-team-meta">
                      <span className="bz-item-name block">{[u.name, u.family_name].filter(Boolean).join(' ') || u.username}</span>
                      <span className="bz-team-email block">{u.email}</span>
                    </span>
                  </span>
                  <span className="flex items-center gap-2 flex-wrap">
                    <span className="bz-role-chips">
                      {roles.length > 0
                        ? roles.map((r) => <span key={r} className="bz-role-chip">{r}</span>)
                        : <span style={{ color: '#9CA3AF' }}>—</span>}
                    </span>
                    <span className={`bz-access-status ${st.cls}`}>{t(st.label)}</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
        <div className="biz-wide hidden md:block" style={{ padding: 0 }}>
          <table className="bz-access-table bz-wide-table">
            <thead>
              <tr>
                <th>{t('team.col.member')}</th>
                <th>{t('team.col.role')}</th>
                <th>{t('team.col.status')}</th>
              </tr>
            </thead>
            <tbody>
              {team.map((u) => {
                const st = teamStatus(u);
                const roles = businessRoles(u, t);
                return (
                  <tr
                    key={u.username}
                    onClick={onOpen ? () => onOpen(u) : undefined}
                    style={onOpen ? { cursor: 'pointer' } : undefined}
                  >
                    <td>
                      <div className="bz-team-person">
                        <span className="bz-team-avatar">{initials(u)}</span>
                        <div className="bz-team-meta">
                          <div className="bz-item-name">
                            {[u.name, u.family_name].filter(Boolean).join(' ') || u.username}
                          </div>
                          <div className="bz-team-email">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="bz-role-chips">
                        {roles.length > 0
                          ? roles.map((r) => <span key={r} className="bz-role-chip">{r}</span>)
                          : <span style={{ color: '#9CA3AF' }}>—</span>}
                      </div>
                    </td>
                    <td>
                      <span className={`bz-access-status ${st.cls}`}>{t(st.label)}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
  );
};

/**
 * The tenant Front Desk adds employees to the business (SAM-1297) but cannot list accounts: the list belongs to the
 * tenant admin. It gets the same "Add member" button and the same sheet, which asks for the ID, position and end date.
 */
const FrontDeskTeam: React.FC<{ businessId: string }> = ({ businessId }) => {
  const { t } = useT();
  const [adding, setAdding] = useState(false);
  const canAddMember = useFdCan('team', 'create');
  const { data: businessesData } = useBusinesses();
  const businesses: Business[] = useMemo(() => {
    const raw = businessesData as Business[] | { data?: Business[] } | undefined;
    if (Array.isArray(raw)) return raw;
    return raw && 'data' in raw && Array.isArray(raw.data) ? raw.data : [];
  }, [businessesData]);
  return (
    <div>
      <div className="bz-pay-filters">
        {canAddMember && (
          <button type="button" className="btn primary" onClick={() => setAdding(true)}>
            <UserPlus size={14} /> {t('team.addMember')}
          </button>
        )}
      </div>
      <AddBusinessUserSheet isOpen={adding} onClose={() => setAdding(false)} businessId={businessId} businesses={businesses} employee />
      <div className="bz-list-empty" role="status">{t('team.frontDeskNote')}</div>
    </div>
  );
};

export const BusinessTeamList: React.FC<{ businessId: string; onOpen?: (member: SAMUser) => void }> = (props) => {
  const { t } = useT();
  const { isLoading, cognitoId, isTenantFrontDesk } = useUserPermissions();
  // Until the groups are read nothing mounts: the account list is a request the tenant Front Desk gets 403 for.
  if (isLoading || !cognitoId) return <TableSkeleton rows={5} bare label={t('team.loading')} />;
  return isTenantFrontDesk ? <FrontDeskTeam businessId={props.businessId} /> : <AdminTeamList {...props} />;
};
